import React from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { Col, Row, Form, Container, Tooltip, OverlayTrigger } from "react-bootstrap";
import { useParams } from "react-router-dom";
import branchApi from "../../../services/branchApi";
import api from "../../../services/httpClient";
import "../../../assets/styles/users.scss";
import "../../../assets/styles/createUserForm.scss";

const initialFormData = {
  isActive: "",
  branchIds: [],
  login: "",
  email: "",
  fullName: "",
  role: "",
};

const normalizeBranchIds = (branches) => {
  if (!Array.isArray(branches)) return [];

  return branches
    .map((branch) => {
      if (branch == null) return null;

      if (typeof branch === "object") {
        return branch.id ?? branch.Id ?? branch.branchId ?? branch.BranchId ?? null;
      }

      return branch;
    })
    .filter((branchId) => branchId != null)
    .map((branchId) => String(branchId));
};

const normalizeIsActiveToFormValue = (value) => {
  if (value == null || value === "") return "";

  if (typeof value === "boolean") {
    return value ? "1" : "0";
  }

  const normalized = String(value).trim().toLowerCase();
  if (["1", "true", "tak", "yes"].includes(normalized)) return "1";
  if (["0", "false", "nie", "no"].includes(normalized)) return "0";

  const numeric = Number(value);
  if (Number.isFinite(numeric)) {
    return String(Number(numeric > 0));
  }

  return "";
};

const normalizeUserToFormData = (user) => ({
  isActive: normalizeIsActiveToFormValue(user?.isActive),
  branchIds: normalizeBranchIds(user?.branchIds ?? user?.branches),
  login: user?.login ?? "",
  email: user?.email ?? "",
  fullName: user?.name ?? user?.fullName ?? "",
  role: user?.userRights ?? user?.role ?? "",
});

const arraysAreEqual = (left, right) => {
  if (left.length !== right.length) return false;

  return left.every((value, index) => value === right[index]);
};

const UpdateUserForms = () => {
  const { id: userId } = useParams();
  const [branches, setBranches] = React.useState([]);
  const [formData, setFormData] = React.useState(initialFormData);
  const [initialLoadedFormData, setInitialLoadedFormData] = React.useState(initialFormData);
  const [saving, setSaving] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const normalizedBranches = Array.isArray(branches)
    ? branches
        .map((branch) => {
          if (!branch) return null;
          if (typeof branch === "string") {
            return { id: branch, name: branch };
          }

          if (typeof branch === "object") {
            return {
              id: branch.id ?? branch.Id,
              name:
                branch.name ??
                branch.Name ??
                String(branch.id ?? branch.Id ?? ""),
            };
          }

          return null;
        })
        .filter((branch) => branch?.id != null)
    : [];

  React.useEffect(() => {
    const controller = new AbortController();

    (async () => {
      try {
        const list = await branchApi.getAllBranches(controller.signal);
        setBranches(Array.isArray(list) ? list : []);
      } catch {
        setBranches([]);
      }
    })();

    return () => controller.abort();
  }, []);

  React.useEffect(() => {
    if (!userId) return undefined;

    const controller = new AbortController();

    (async () => {
      try {
        setLoading(true);
        const response = await api.get(`/User/${userId}`, {
          signal: controller.signal,
        });

        const user = response?.data?.data ?? response?.data ?? null;
        const normalizedUser = normalizeUserToFormData(user);

        setFormData(normalizedUser);
        setInitialLoadedFormData(normalizedUser);
      } catch (error) {
        if (error?.name === "CanceledError" || error?.code === "ERR_CANCELED") {
          return;
        }

        console.error("Error loading user:", error);
        alert("Wystąpił błąd podczas pobierania danych użytkownika.");
      } finally {
        setLoading(false);
      }
    })();

    return () => controller.abort();
  }, [userId]);

  const handleReset = (event) => {
    event.preventDefault();

    setFormData(initialLoadedFormData);
  };

  const handleFieldChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleBranchChange = (event) => {
    const selectedValues = Array.from(
      event.target.selectedOptions,
      (option) => option.value,
    );

    setFormData((prev) => ({
      ...prev,
      branchIds: selectedValues,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const normalizedCurrentFormData = {
      isActive: normalizeIsActiveToFormValue(formData.isActive),
      role: formData.role,
      login: formData.login.trim(),
      fullName: formData.fullName.trim(),
      email: formData.email.trim(),
      branchIds: formData.branchIds.map((id) => String(id)),
    };

    const payload = {};

    const initialIsActive = normalizeIsActiveToFormValue(initialLoadedFormData.isActive);
    const currentIsActive = normalizeIsActiveToFormValue(normalizedCurrentFormData.isActive);
    const resolvedIsActive = currentIsActive === "" ? initialIsActive : currentIsActive;

    if (resolvedIsActive !== "") {
      payload.isActive = Number(resolvedIsActive);
    }

    if (normalizedCurrentFormData.role !== initialLoadedFormData.role) {
      payload.userRights = normalizedCurrentFormData.role;
    }

    if (normalizedCurrentFormData.login !== initialLoadedFormData.login) {
      payload.login = normalizedCurrentFormData.login;
    }

    if (normalizedCurrentFormData.fullName !== initialLoadedFormData.fullName) {
      payload.name = normalizedCurrentFormData.fullName;
    }

    if (normalizedCurrentFormData.email !== initialLoadedFormData.email) {
      payload.email = normalizedCurrentFormData.email;
    }

    if (
      !arraysAreEqual(
        normalizedCurrentFormData.branchIds,
        initialLoadedFormData.branchIds,
      )
    ) {
      payload.branchIds = normalizedCurrentFormData.branchIds.map((id) => Number(id));
    }

    if (!userId) {
      alert("Nie znaleziono identyfikatora użytkownika.");
      return;
    }

    if (Object.keys(payload).length === 0) {
      alert("Brak zmian do zapisania.");
      return;
    }

    try {
      setSaving(true);
      await api.put(`/User/${userId}`, payload);
      alert("Użytkownik został zaktualizowany.");
      setInitialLoadedFormData(normalizedCurrentFormData);
      setFormData(normalizedCurrentFormData);
    } catch (error) {
      console.error("Error updating user:", error);
      if (error?.response?.status === 423) {
        alert("Baza danych jest obecnie zablokowana. Zmiana nie może zostać wykonana");
      } else {
        alert("Wystąpił błąd podczas aktualizacji użytkownika.");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container fluid="md" className="create-user-form">
      <div className="create-user-form__card">
        <div className="create-user-form__header mb-4">
          <div>
            <h4 className="create-user-form__title">Edycja użytkownika</h4>
            <p className="create-user-form__subtitle">
              Uzupełnij dane użytkownika i wybierz przypisany oddział oraz rolę.
            </p>
          </div>
        </div>

  <Form className="create-user-form__body" onReset={handleReset} onSubmit={handleSubmit}>
          <Form.Group
            as={Row}
            className="create-user-form__group"
            controlId="formSelect1"
          >
            <Form.Label
              column
              lg={4}
              sm={12}
              className="create-user-form__label"
            >
              Aktywny
            </Form.Label>
            <Col lg={8} sm={12}>
              <Form.Select
                className="create-user-form__control"
                value={formData.isActive}
                onChange={handleFieldChange("isActive")}
                disabled={loading}
              >
                <option value="">---Wybierz---</option>
                <option value={1}>Tak</option>
                <option value={0}>Nie</option>
              </Form.Select>
            </Col>
          </Form.Group>

          <Form.Group
            as={Row}
            className="create-user-form__group"
            controlId="formSelect2"
          >
            <Form.Label
              column
              lg={4}
              sm={12}
              className="create-user-form__label"
            >
              Oddział DHL [EX DR]
            </Form.Label>
            <Col lg={8} sm={12}>
              <OverlayTrigger
                overlay={
                  <Tooltip id="branches-tooltip">
                    Przytrzymaj CTRL+C aby wybrać wiele.

                  </Tooltip>
                }
              >
                <Form.Select
                  className="create-user-form__control"
                  multiple
                  value={formData.branchIds}
                  onChange={handleBranchChange}
                >
                  {normalizedBranches.map((branch) => (
                    <option key={branch.id} value={branch.id}>
                      {branch.name}
                    </option>
                  ))}
                </Form.Select>
              </OverlayTrigger>
            </Col>
          </Form.Group>

          <Form.Group
            as={Row}
            className="create-user-form__group"
            controlId="formInput1"
          >
            <Form.Label
              column
              lg={4}
              sm={12}
              className="create-user-form__label"
            >
              Login
            </Form.Label>
            <Col lg={8} sm={12}>
              <Form.Control
                type="text"
                placeholder="Wprowadź login"
                className="create-user-form__control"
                value={formData.login}
                onChange={handleFieldChange("login")}
                disabled={loading}
              />
            </Col>
          </Form.Group>

          <Form.Group
            as={Row}
            className="create-user-form__group"
            controlId="formInput2"
          >
            <Form.Label
              column
              lg={4}
              sm={12}
              className="create-user-form__label"
            >
              Email
            </Form.Label>
            <Col lg={8} sm={12}>
              <Form.Control
                type="email"
                placeholder="Wprowadź adres email"
                className="create-user-form__control"
                value={formData.email}
                onChange={handleFieldChange("email")}
                disabled={loading}
              />
            </Col>
          </Form.Group>

          <Form.Group
            as={Row}
            className="create-user-form__group"
            controlId="formInput3"
          >
            <Form.Label
              column
              lg={4}
              sm={12}
              className="create-user-form__label"
            >
              Imię i Nazwisko
            </Form.Label>
            <Col lg={8} sm={12}>
              <Form.Control
                type="text"
                placeholder="Wprowadź imię i nazwisko"
                className="create-user-form__control"
                value={formData.fullName}
                onChange={handleFieldChange("fullName")}
                disabled={loading}
              />
            </Col>
          </Form.Group>

          <Form.Group
            as={Row}
            className="create-user-form__group"
            controlId="formSelectRole"
          >
            <Form.Label
              column
              lg={4}
              sm={12}
              className="create-user-form__label"
            >
              Rola
            </Form.Label>
            <Col lg={8} sm={12}>
              <Form.Select
                className="create-user-form__control"
                value={formData.role}
                onChange={handleFieldChange("role")}
                disabled={loading}
              >
                <option value="">---Wybierz---</option>
                <option value="ROLE_USER">User</option>
                <option value="ROLE_SUPERUSER">Power User</option>
              </Form.Select>
            </Col>
          </Form.Group>

          <Row className="create-user-form__actions">
            <Col sm="auto" className="create-user-form__actions-col">
              <button
                type="reset"
                className="btn create-user-form__btn create-user-form__btn--reset"
                disabled={loading || saving}
              >
                Reset
              </button>
              <button
                type="submit"
                className="btn create-user-form__btn create-user-form__btn--save"
                disabled={loading || saving}
              >
                {loading ? "Ładowanie..." : saving ? "Zapisywanie..." : "Zapisz"}
              </button>
            </Col>
          </Row>
        </Form>
      </div>
    </Container>
  );
};

export default UpdateUserForms;
